import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import styles from './users.module.css';
import { adminAPI } from '../utils/adminAPI';

export const UserDetailPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [trips, setTrips] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [activeTab, setActiveTab] = useState('overview');

    const fetchAllUserData = useCallback(async () => {
        try {
            setLoading(true);
            
            // Fetch all data in parallel
            const [usersData, tripsData, expensesData, messagesData] = await Promise.all([
                adminAPI.users(),
                adminAPI.trips(),
                adminAPI.expenses(),
                adminAPI.messages(),
            ]);

            const numUserId = parseInt(userId);

            // Find user
            const foundUser = usersData.users?.find(u => u.id === numUserId);
            if (foundUser) {
                setUser(foundUser);
                setEditData(foundUser);
            } else {
                setError('User not found');
            }

            // Filter user data
            const userTrips = (tripsData.trips || []).filter(trip => trip.user_id === numUserId);
            setTrips(userTrips);

            const userExpenses = (expensesData.expenses || []).filter(exp => exp.user_id === numUserId);
            setExpenses(userExpenses);

            const userMessages = (messagesData.messages || []).filter(msg => msg.user_id === numUserId);
            setMessages(userMessages);
        } catch (err) {
            console.error("Error fetching data:", err);
            setError('Error loading user data');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchAllUserData();
    }, [fetchAllUserData]);

    const handleDeleteUser = async () => {
        if (!confirm(`Are you sure you want to delete ${user.firstname} ${user.lastname}? This action cannot be undone.`)) {
            return;
        }
        try {
            await adminAPI.deleteUser(user.id);
            navigate('/admin/users');
        } catch (err) {
            console.error('Error deleting user:', err);
            setError('Failed to delete user');
        }
    };

    const handleSaveChanges = async () => {
        try {
            setUser(editData);
            setIsEditing(false);
        } catch (err) {
            console.error("Error updating user:", err);
            setError('Error updating user');
        }
    };

    if (loading) {
        return <div className={styles.main}><p>Loading...</p></div>;
    }

    if (error && !user) {
        return (
            <div className={styles.main}>
                <Link to="/admin/users" className={styles.adminLink}>Back to Users</Link>
                <p style={{ color: 'red' }}>{error}</p>
            </div>
        );
    }

    if (!user) {
        return (
            <div className={styles.main}>
                <Link to="/admin/users" className={styles.adminLink}>Back to Users</Link>
                <p>User not found</p>
            </div>
        );
    }

    const totalExpenses = expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
    const totalTrips = trips.length;
    const totalMessages = messages.length;

    return (
        <main className={styles.main}>
            <div className={styles.pageHeader}>
                <Link to="/admin/users" className={styles.adminLink}>← Back</Link>
                <h2 className={styles.h2}>{user.firstname} {user.lastname}</h2>
                <div style={{ visibility: 'hidden' }} className={styles.adminLink}></div>
            </div>

            {error && <div style={{color: 'red', padding: '10px', backgroundColor: '#ffe0e0', borderRadius: '4px', marginBottom: '20px'}}>{error}</div>}

            {/* Tabs Navigation */}
            <div className={styles.tabNav}>
                <button className={`${styles.tabBtn} ${activeTab === 'overview' ? styles.active : ''}`} onClick={() => setActiveTab('overview')}>
                    Overview
                </button>
                <button className={`${styles.tabBtn} ${activeTab === 'trips' ? styles.active : ''}`} onClick={() => setActiveTab('trips')}>
                    Trips ({totalTrips})
                </button>
                <button className={`${styles.tabBtn} ${activeTab === 'expenses' ? styles.active : ''}`} onClick={() => setActiveTab('expenses')}>
                    Expenses ({expenses.length})
                </button>
                <button className={`${styles.tabBtn} ${activeTab === 'messages' ? styles.active : ''}`} onClick={() => setActiveTab('messages')}>
                    Messages ({totalMessages})
                </button>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <div className={styles.detailContainer}>
                    {/* Stats Cards */}
                    <div className={styles.statsGrid}>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{totalTrips}</div>
                            <div className={styles.statLabel}>Total Trips</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>${totalExpenses.toFixed(2)}</div>
                            <div className={styles.statLabel}>Total Spent</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{totalMessages}</div>
                            <div className={styles.statLabel}>Messages</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{user.is_active ? 'Active' : 'Inactive'}</div>
                            <div className={styles.statLabel}>Status</div>
                        </div>
                    </div>

                    {/* Personal Information */}
                    <section className={styles.detailSection}>
                        <h3>Personal Information</h3>
                        <div className={styles.detailGrid}>
                            <div className={styles.detailField}>
                                <label>ID:</label>
                                <p>{user.id}</p>
                            </div>
                            <div className={styles.detailField}>
                                <label>First Name:</label>
                                {isEditing ? (
                                    <input type="text" value={editData.firstname || ''} onChange={(e) => setEditData({ ...editData, firstname: e.target.value })} />
                                ) : (
                                    <p>{user.firstname}</p>
                                )}
                            </div>
                            <div className={styles.detailField}>
                                <label>Last Name:</label>
                                {isEditing ? (
                                    <input type="text" value={editData.lastname || ''} onChange={(e) => setEditData({ ...editData, lastname: e.target.value })} />
                                ) : (
                                    <p>{user.lastname}</p>
                                )}
                            </div>
                            <div className={styles.detailField}>
                                <label>Email:</label>
                                {isEditing ? (
                                    <input type="email" value={editData.email || ''} onChange={(e) => setEditData({ ...editData, email: e.target.value })} />
                                ) : (
                                    <p>{user.email}</p>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* Account Status */}
                    <section className={styles.detailSection}>
                        <h3>Account Status</h3>
                        <div className={styles.detailGrid}>
                            <div className={styles.detailField}>
                                <label>Role:</label>
                                <p>{user.role}</p>
                            </div>
                            <div className={styles.detailField}>
                                <label>Active:</label>
                                <p>{user.is_active ? 'Yes' : 'No'}</p>
                            </div>
                            <div className={styles.detailField}>
                                <label>Email Verified:</label>
                                <p>{user.email_verified ? 'Yes' : 'No'}</p>
                            </div>
                            <div className={styles.detailField}>
                                <label>Current Trip ID:</label>
                                <p>{user.current_trip_id ? <Link to={`/admin/trips`}>{user.current_trip_id}</Link> : 'None'}</p>
                            </div>
                        </div>
                    </section>

                    {/* Activity Dates */}
                    <section className={styles.detailSection}>
                        <h3>Activity Dates</h3>
                        <div className={styles.detailGrid}>
                            <div className={styles.detailField}>
                                <label>Created At:</label>
                                <p>{new Date(user.created_at).toLocaleString()}</p>
                            </div>
                            <div className={styles.detailField}>
                                <label>Updated At:</label>
                                <p>{new Date(user.updated_at).toLocaleString()}</p>
                            </div>
                            <div className={styles.detailField}>
                                <label>Last Login:</label>
                                <p>{user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}</p>
                            </div>
                        </div>
                    </section>

                    {/* Actions */}
                    <section className={styles.detailSection}>
                        <h3>Actions</h3>
                        <div className={styles.actionButtons}>
                            {isEditing ? (
                                <>
                                    <button className={styles.btnPrimary} onClick={handleSaveChanges}>Save Changes</button>
                                    <button className={styles.btnSecondary} onClick={() => { setIsEditing(false); setEditData(user); }}>Cancel</button>
                                </>
                            ) : (
                                <button className={styles.btnPrimary} onClick={() => setIsEditing(true)}>Edit User</button>
                            )}
                            <button className={styles.btnDanger} onClick={handleDeleteUser}>Delete User</button>
                        </div>
                    </section>
                </div>
            )}

            {/* Trips Tab */}
            {activeTab === 'trips' && (
                <div className={styles.detailContainer}>
                    <section className={styles.detailSection}>
                        <h3>User Trips ({trips.length})</h3>
                        {trips.length === 0 ? (
                            <p style={{color: '#666'}}>No trips found.</p>
                        ) : (
                            <div className={styles.dataTable}>
                                <table className={styles.userTable}>
                                    <thead>
                                        <tr>
                                            <th>Trip ID</th>
                                            <th>Name</th>
                                            <th>Destination</th>
                                            <th>Start Date</th>
                                            <th>End Date</th>
                                            <th>Budget</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {console.log('trips',trips)}
                                        {trips.map(trip => (
                                            <tr key={trip.id}>
                                                <td>{trip.id}</td>
                                                <td>{trip.trip_name}</td>
                                                <td>{trip.destination}</td>
                                                <td>{new Date(trip.start_date).toLocaleDateString()}</td>
                                                <td>{new Date(trip.end_date).toLocaleDateString()}</td>
                                                <td>${parseFloat(trip.budget)?.toFixed(2)}</td>
                                                <td>
                                                    <Link to={`/admin/trips`} className={styles.linkBtn}>View</Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </div>
            )}

            {/* Expenses Tab */}
            {activeTab === 'expenses' && (
                <div className={styles.detailContainer}>
                    <section className={styles.detailSection}>
                        <h3>User Expenses ({expenses.length})</h3>
                        <div className={styles.expenseStats}>
                            <div className={styles.expenseStat}>
                                <span>Total Expenses:</span>
                                <strong>${totalExpenses.toFixed(2)}</strong>
                            </div>
                            <div className={styles.expenseStat}>
                                <span>Average Per Expense:</span>
                                <strong>${expenses.length > 0 ? (totalExpenses / expenses.length).toFixed(2) : '0.00'}</strong>
                            </div>
                        </div>
                        {expenses.length === 0 ? (
                            <p style={{color: '#666'}}>No expenses found.</p>
                        ) : (
                            <div className={styles.dataTable}>
                                <table className={styles.userTable}>
                                    <thead>
                                        <tr>
                                            <th>Trip ID</th>
                                            <th>Category</th>
                                            <th>Description</th>
                                            <th>Amount</th>
                                            <th>Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {expenses.map(exp => (
                                            <tr key={exp.id}>
                                                <td>{exp.trip_id}</td>
                                                <td>{exp.category || 'N/A'}</td>
                                                <td>{exp.description}</td>
                                                <td>${parseFloat(exp.amount).toFixed(2)}</td>
                                                <td>{new Date(exp.created_at).toLocaleDateString()}</td>
                                                <td>
                                                    <Link to={`/admin/expenses`} className={styles.linkBtn}>View</Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </div>
            )}

            {/* Messages Tab */}
            {activeTab === 'messages' && (
                <div className={styles.detailContainer}>
                    <section className={styles.detailSection}>
                        <h3>User Messages ({messages.length})</h3>
                        {messages.length === 0 ? (
                            <p style={{color: '#666'}}>No messages found.</p>
                        ) : (
                            <div className={styles.messagesList}>
                                {messages.map(msg => (
                                    <div key={msg.id} className={styles.messageItem}>
                                        <div className={styles.messageHeader}>
                                            <strong>{msg.name}</strong>
                                            <span className={styles.messageDate}>{new Date(msg.created_at).toLocaleString()}</span>
                                        </div>
                                        <div className={styles.messageEmail}>Email: {msg.email}</div>
                                        <div className={styles.messageContent}>{msg.message}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>
            )}
        </main>
    );
};
